import React from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';
import { formatThaiDate } from './DateRangePicker';

/**
 * AuditTrail — ใคร ทำอะไร เมื่อไร จากค่าเดิมเป็นค่าใหม่ (FR-ADM-37)
 *
 * ทำเป็น component เดียวเพราะข้อมูลชุดนี้ปรากฏใน 6 หน้า
 * (Customer 360 · คำขอ · กติกาคะแนน · Tier · ผู้ใช้งาน · Audit Log กลาง)
 * ถ้าไม่รวมไว้ที่เดียวจะแสดงคนละแบบทั้ง 6 หน้า
 */
export interface AuditChange {
  field: string;
  /** ค่าเดิม — null = ยังไม่มีค่า (การสร้างครั้งแรก) */
  before: string | null;
  after: string | null;
}

export interface AuditEntry {
  id: string;
  /** ISO timestamp */
  at: string;
  actorName: string;
  actorRole: string;
  /** สิ่งที่ทำ เขียนเป็นภาษาไทยอ่านรู้เรื่อง เช่น 'อนุมัติคำขอสะสมคะแนน' */
  action: string;
  /** ไอคอน Material Symbols — บังคับ เพื่อไม่ให้แยกประเภทด้วยสีเดียว */
  icon?: string;
  /** เหตุผลที่กรอกตอนทำ (ถ้ามี) */
  reason?: string;
  changes?: AuditChange[];
  /** ระบบทำเอง ไม่ใช่คน เช่น การตรวจ Order ID อัตโนมัติ */
  automated?: boolean;
  /** การสร้างครั้งแรก — ไม่มีค่าเดิม ตารางแสดงแค่ "ฟิลด์ / ค่าที่ตั้ง" */
  initial?: boolean;
}

/**
 * ⚠️ วันที่ต้องมาจากเวลาท้องถิ่นเหมือนชั่วโมง — `toISOString()` เป็นเวลา UTC
 *    เดิมใช้ปนกันจนรายการช่วง 00:00–06:59 ขึ้นเป็นวันก่อนหน้า
 */
const formatTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${formatThaiDate(ymd)} ${hh}:${mm}`;
};

export interface AuditTrailProps {
  entries: AuditEntry[];
  /** ข้อความเมื่อไม่มีประวัติ */
  emptyLabel?: string;
  /** แสดงย่อ (ไม่กางรายการค่าที่เปลี่ยน) — ใช้ใน SideDrawer ที่พื้นที่จำกัด */
  compact?: boolean;
  className?: string;
}

export const AuditTrail: React.FC<AuditTrailProps> = ({
  entries, emptyLabel = 'ยังไม่มีประวัติการแก้ไข', compact = false, className,
}) => {
  if (entries.length === 0) {
    return (
      <p className={cn('font-[family-name:var(--font-thai)] text-[length:var(--text-form)] leading-[var(--leading-thai)] text-[var(--ink-600)]', className)}>
        {emptyLabel}
      </p>
    );
  }

  return (
    <ol className={cn('flex flex-col font-[family-name:var(--font-thai)]', className)}>
      {entries.map((e, i) => (
        <li key={e.id} className="relative flex gap-[var(--space-3)] pb-[var(--space-4)]">
          {i < entries.length - 1 && (
            <span aria-hidden className="absolute left-[13px] top-[28px] h-full w-px bg-[var(--line-200)]" />
          )}

          <span
            aria-hidden
            className={cn(
              'relative z-[1] flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full border',
              e.automated
                ? 'border-[var(--line-300)] bg-[var(--surface-100)] text-[var(--ink-600)]'
                : 'border-[var(--blue-200)] bg-[var(--blue-050)] text-[var(--blue-700)]',
            )}
          >
            <Icon name={e.icon ?? (e.automated ? 'bolt' : 'person')} size={16} filled />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-[length:var(--text-form)] leading-[var(--leading-form)] text-[var(--ink-900)]">
              <span className="font-[var(--weight-semibold)]">{e.action}</span>
              {' · '}
              <span className="text-[var(--ink-700)]">
                {e.automated ? 'ระบบทำอัตโนมัติ' : `${e.actorName} (${e.actorRole})`}
              </span>
            </p>
            <p className="bo-num text-[length:var(--text-table-sm)] leading-[var(--leading-table)] text-[var(--ink-600)]">
              {formatTime(e.at)}
            </p>

            {e.reason && (
              <p className="mt-[var(--space-1)] rounded-[var(--radius-sm)] bg-[var(--surface-100)] px-[var(--space-2)] py-[var(--space-1)] text-[length:var(--text-table)] leading-[var(--leading-thai)] text-[var(--ink-700)]">
                <span className="text-[var(--ink-600)]">เหตุผล: </span>{e.reason}
              </p>
            )}

            {!compact && e.changes && e.changes.length > 0 && (
              <table className="mt-[var(--space-2)] w-full text-[length:var(--text-table-sm)] leading-[var(--leading-table)]">
                <caption className="sr-only">รายการค่าที่เปลี่ยน</caption>
                <thead>
                  <tr className="text-left text-[var(--ink-600)]">
                    <th scope="col" className="py-[var(--space-05)] pr-[var(--space-2)] font-[var(--weight-medium)]">ฟิลด์</th>
                    {!e.initial && (
                      <th scope="col" className="py-[var(--space-05)] pr-[var(--space-2)] font-[var(--weight-medium)]">ค่าเดิม</th>
                    )}
                    <th scope="col" className="py-[var(--space-05)] font-[var(--weight-medium)]">{e.initial ? 'ค่าที่ตั้ง' : 'ค่าใหม่'}</th>
                  </tr>
                </thead>
                <tbody>
                  {e.changes.map((c) => (
                    <tr key={c.field} className="border-t border-[var(--line-200)] align-top">
                      <td className="py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-700)]">{c.field}</td>
                      {!e.initial && (
                        /* ขีดฆ่าเฉพาะค่าที่มีจริง — "—" ขีดฆ่าอ่านเหมือนมีค่าถูกลบ */
                        <td className={cn('py-[var(--space-1)] pr-[var(--space-2)] text-[var(--ink-600)]', c.before !== null && 'line-through')}>
                          {c.before ?? '—'}
                        </td>
                      )}
                      <td className="py-[var(--space-1)] font-[var(--weight-medium)] text-[var(--ink-900)]">{c.after ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};
